'use client';
import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  calculateWrestlerData,
  calculateRangeSliderMaxValueByRarity,
} from '../../utils/superstar-details/helper';

function createEquippedAbilities(abilities: Ability[]) {
  if (!abilities || (abilities && !abilities.length)) return [];

  const equipped: { ability_id: string; level: number }[] = [];

  for (let i = 0; i < 3; i++) {
    const ability = abilities[i];
    if (ability?.ID && ability?.levelCap !== undefined) {
      equipped.push({ ability_id: ability.ID, level: ability.levelCap });
    }
  }

  return equipped;
}

import {
  SuperstarDetails,
  SuperstarAvatarResponse,
} from '@wwe-portal/entities/superstar.type';
import SuperstarContentDetails from './SuperstarContentDetails';
import { useSuperstarDetailsLegacy } from '@wwe-portal/web/providers/queries/superstar';
import { DateTime } from 'luxon';
import { extractSuperstarName } from '../../utils/superstar-details/extractor';
import { sendSuperStarEvent } from '../../libs/analytics/superstars';

const chart1Data = {
  borderColor: '#00A0FF',
  data: [] as number[],
  firstIcon: 'compass-empty',
  firstIconTitle: 0,
  gradientZero: 'rgba(0, 160, 255, 0.08)',
  id: 1,
  secondIcon: 'compass-fill',
  secondIconTitle: 179389,
  title: 'total_upgrades',
};

const chart2Data = {
  borderColor: '#FFA800',
  data: [] as number[],
  firstIcon: 'compass-empty',
  firstIconTitle: 0,
  gradientZero: 'rgba(227, 152, 8, 0.3)',
  id: 2,
  secondIcon: 'compass-fill',
  secondIconTitle: 179389,
  title: 'total_wins',
};

interface Ability {
  MovePoint: number;
  name: string;
  gemType: string;
  ability_id: string;
  ID: string;
  levelCap: number;
  level: number;
  text: string;
}

function SuperStarDetailsContent({
  avatarData,
  id,
}: {
  avatarData: SuperstarAvatarResponse;
  id: string;
}) {
  const [globalDetails, setGlobalDetails] = useState<SuperstarDetails | null>(
    null
  );

  const [wrestlerTier, setWrestlerTier] = useState<
    'Bronze' | 'Silver' | 'Gold'
  >('Bronze');

  const [starRarity, setStarRarity] = useState('1_1');

  const [rangeSliderMaxValue, setRangeSliderMaxValue] = useState(10);

  const [rangeSliderValue, setRangeSliderValue] = useState(10);

  const [abilities, setAbilities] = useState<Ability[]>([]);

  const [equippedAbilities, setEquippedAbilities] = useState<
    { ability_id: string; level: number }[]
  >([]);

  const [XLabel, setXLabel] = useState<string[]>([]);

  const [disableEquipButton, setDisableEquipButton] = useState(false);
  const { data, isFetching: isLoading } = useSuperstarDetailsLegacy({ id });
  const wrestlerDetails = data?.data;
  useEffect(() => {
    setDisableEquipButton(equippedAbilities?.length >= 3);
  }, [equippedAbilities]);

  useEffect(() => {
    if (wrestlerDetails) {
      const { mainString, subString } =
        extractSuperstarName(wrestlerDetails.name) ?? {};

      sendSuperStarEvent({
        mainString,
        subString,
      });
    }
  }, [wrestlerDetails]);

  useEffect(() => {
    const crawlAbility = wrestlerDetails?.[wrestlerTier][starRarity].Abilities;
    if (crawlAbility) {
      setAbilities(crawlAbility);
      const equipped = createEquippedAbilities(
        wrestlerDetails[wrestlerTier][starRarity].Abilities
      );
      return setEquippedAbilities(equipped);
    }
    setAbilities([]);
    setEquippedAbilities([]);
  }, [wrestlerTier, starRarity, wrestlerDetails]);

  useEffect(() => {
    const details = calculateWrestlerData(
      wrestlerTier,
      starRarity,
      rangeSliderValue,
      wrestlerDetails,
      equippedAbilities
    );

    setGlobalDetails(details as any);
  }, [
    wrestlerTier,
    starRarity,
    rangeSliderValue,
    wrestlerDetails,
    equippedAbilities,
  ]);

  const wrestlerTierDropdownItemClick = useCallback(
    (tier: 'Bronze' | 'Silver' | 'Gold') => {
      setWrestlerTier(tier);

      if (wrestlerDetails) {
        const crawlMaxValue = wrestlerDetails[tier][starRarity].LevelCap;
        const maxValue =
          crawlMaxValue ??
          calculateRangeSliderMaxValueByRarity(starRarity, tier);
        setRangeSliderValue(maxValue);
        setRangeSliderMaxValue(maxValue);
        setEquippedAbilities([]);
      }
    },
    [starRarity, wrestlerDetails]
  );

  const starRarityDropdownItemClick = useCallback(
    (level: string) => {
      setStarRarity(level);
      if (wrestlerDetails) {
        const crawlMaxValue = wrestlerDetails[wrestlerTier][level].LevelCap;
        const maxValue =
          crawlMaxValue ??
          calculateRangeSliderMaxValueByRarity(level, wrestlerTier);

        setRangeSliderValue(maxValue);
        setRangeSliderMaxValue(maxValue);
        setEquippedAbilities([]);
      }
    },
    [wrestlerTier, wrestlerDetails]
  );

  const handleWrestlerLevelChange = useCallback(
    (value: number) => setRangeSliderValue(value),
    []
  );

  const handleAbilityEquipChange = useCallback(
    (flag: boolean, ability_id: string, level: number) => {
      let equipped = [...equippedAbilities];

      if (equipped.length) {
        abilities.forEach((ability) => {
          if (ability.ID === ability_id) {
            if (!flag) {
              equipped.push({ ability_id, level });
            } else {
              equipped = equipped.filter(
                (ability) => ability.ability_id !== ability_id
              );
            }
          }
        });
      } else {
        if (!flag) {
          equipped.push({ ability_id, level });
        }
      }

      setEquippedAbilities(equipped);
    },
    [equippedAbilities, abilities]
  );

  const handleAbilityLevelChange = useCallback(
    (ability_id: string, level: number) => {
      const equipped = [...equippedAbilities];

      if (equipped.length) {
        equipped.map((ability) => {
          if (ability.ability_id === ability_id) {
            ability.level = level;
          }
          return ability;
        });
        setTimeout(() => {
          setEquippedAbilities(equipped);
        }, 1000);
      }
    },
    [equippedAbilities]
  );

  useEffect(() => {
    const arr: string[] = [];
    const data1: number[] = [];
    const data2: number[] = [];
    let allUgrades = 0;
    let allWins = 0;
    if (
      wrestlerDetails?.progression &&
      wrestlerDetails.progression.length > 0
    ) {
      for (const item of wrestlerDetails.progression) {
        if (item.snapshot_month) {
          arr.push(DateTime.fromISO(item.snapshot_month).toFormat('MMM'));
        }
        if (item.talent_upgrade) {
          allUgrades += Number(item.talent_upgrade);
          data1.push(Number(item.talent_upgrade));
        }
        if (item.wins) {
          allWins += Number(item.wins);
          data2.push(Number(item.wins));
        }
      }
    }

    setXLabel(arr);

    if (wrestlerDetails?.progression?.length) {
      chart1Data.data = data1;
      chart2Data.data = data2;
      chart1Data.firstIconTitle = Number(
        wrestlerDetails.progression[wrestlerDetails.progression.length - 1]
          ?.talent_upgrade ?? 0
      );
      chart2Data.firstIconTitle = Number(
        wrestlerDetails?.progression?.[wrestlerDetails.progression.length - 1]
          ?.wins ?? 0
      );
      chart1Data.secondIconTitle = allUgrades;
      chart2Data.secondIconTitle = allWins;
    }
  }, [wrestlerDetails]);

  return (
    <SuperstarContentDetails
      wrestlerDetails={wrestlerDetails}
      chart1Data={chart1Data}
      XLabel={XLabel}
      chart2Data={chart2Data}
      rangeSliderValue={rangeSliderValue}
      handleWrestlerLevelChange={handleWrestlerLevelChange}
      rangeSliderMaxValue={rangeSliderMaxValue}
      globalDetails={globalDetails}
      abilities={abilities}
      handleAbilityEquipChange={handleAbilityEquipChange}
      handleAbilityLevelChange={handleAbilityLevelChange}
      disableEquipButton={disableEquipButton}
      wrestlerTier={wrestlerTier}
      starRarity={starRarity}
      starRarityDropdownItemClick={starRarityDropdownItemClick}
      wrestlerTierDropdownItemClick={wrestlerTierDropdownItemClick}
      videoLinks={wrestlerDetails?.videoLinks ?? []}
      avatarData={avatarData.data}
      isLoading={isLoading}
    />
  );
}

export default memo(SuperStarDetailsContent);
